import React from 'react';
import { Box, Skeleton, Grid, Card, CardContent, Fade, Typography } from '@mui/material';

interface CalendarSkeletonProps {
  showDaySummary?: boolean;
  animate?: boolean;
  showFilters?: boolean;
}

export const CalendarSkeleton: React.FC<CalendarSkeletonProps> = ({ 
  showDaySummary = false,
  animate = true,
  showFilters = true
}) => {
  return (
    <Fade in timeout={300}>
      <Box sx={{ width: '100%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Skeleton variant="text" width={200} height={40} animation={animate ? 'wave' : false} />
          <Skeleton variant="rectangular" width={120} height={36} animation={animate ? 'wave' : false} />
        </Box>
        
        {showFilters && (
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3 }}>
            <Skeleton variant="rectangular" width={250} height={56} animation={animate ? 'wave' : false} />
            <Skeleton variant="rectangular" width={180} height={36} animation={animate ? 'wave' : false} />
          </Box>
        )}

        <Card>
          <CardContent>
            <Grid container spacing={1} sx={{ mb: 1 }}>
              {Array.from({ length: 7 }).map((_, index) => (
                <Grid item xs key={index} sx={{ textAlign: 'center' }}>
                  <Skeleton variant="text" width="100%" height={24} animation={animate ? 'wave' : false} />
                </Grid>
              ))}
            </Grid>

            {Array.from({ length: 6 }).map((_, weekIndex) => (
              <Grid container spacing={1} key={weekIndex} sx={{ mb: 1 }}>
                {Array.from({ length: 7 }).map((_, dayIndex) => (
                  <Grid item xs key={dayIndex}>
                    <Box sx={{ height: 80, border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
                      <Skeleton variant="text" width={20} height={20} animation={animate ? 'wave' : false} />
                      <Skeleton variant="rectangular" width="80%" height={4} animation={animate ? 'wave' : false} />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            ))}
          </CardContent>
        </Card>
      </Box>
    </Fade>
  );
};

export const CalendarGridSkeleton: React.FC<{ animate?: boolean }> = ({ animate = true }) => {
  return (
    <Card>
      <CardContent>
        <Grid container spacing={1} sx={{ mb: 1 }}>
          {Array.from({ length: 7 }).map((_, index) => (
            <Grid item xs key={index} sx={{ textAlign: 'center' }}>
              <Skeleton variant="text" width="100%" height={24} animation={animate ? 'wave' : false} />
            </Grid>
          ))}
        </Grid>
        {Array.from({ length: 6 }).map((_, weekIndex) => (
          <Grid container spacing={1} key={weekIndex} sx={{ mb: 1 }}>
            {Array.from({ length: 7 }).map((_, dayIndex) => (
              <Grid item xs key={dayIndex}>
                <Box sx={{ height: 80, border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
                  <Skeleton variant="text" width={20} height={20} animation={animate ? 'wave' : false} />
                </Box>
              </Grid>
            ))}
          </Grid>
        ))}
      </CardContent>
    </Card>
  );
};

export const DaySummaryPanelSkeleton: React.FC<{ animate?: boolean }> = ({ animate = true }) => {
  return (
    <Box sx={{ position: 'fixed', right: 0, top: 0, height: '100vh', width: { xs: '100%', md: 400 }, bgcolor: 'background.paper', boxShadow: 3, zIndex: 1300 }}>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Skeleton variant="text" width={200} height={32} animation={animate ? 'wave' : false} />
      </Box>
      <Box sx={{ p: 2 }}>
        {Array.from({ length: 3 }).map((_, index) => (
          <Card key={index} sx={{ mb: 2 }}>
            <CardContent>
              <Skeleton variant="rectangular" width={60} height={60} animation={animate ? 'wave' : false} />
              <Skeleton variant="text" width="100%" height={24} animation={animate ? 'wave' : false} />
            </CardContent>
          </Card>
        ))}
      </Box>
    </Box>
  );
};

export const FiltersSkeleton: React.FC<{ animate?: boolean }> = ({ animate = true }) => {
  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3 }}>
      <Skeleton variant="rectangular" width={250} height={56} animation={animate ? 'wave' : false} />
      <Skeleton variant="rectangular" width={180} height={36} animation={animate ? 'wave' : false} />
    </Box>
  );
};

export const LoadingMessage: React.FC<{ message?: string; animate?: boolean }> = ({ 
  message = 'Loading calendar...', 
  animate = true 
}) => {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
      <Skeleton variant="circular" width={20} height={20} animation={animate ? 'wave' : false} />
      <Typography variant="body2" color="text.secondary">{message}</Typography>
    </Box>
  );
};

export default CalendarSkeleton;
